import { createExportColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { CONTACTS_COLUMNS } from 'components/Tables/ContactsTable/contactsColumnInfo'
import { BaseExportModal, ExportModalProps } from './ExportModal'
import { useMemo } from 'react'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useDownload } from 'features/download-widget'
import { getDownloadStatus, startContactsExportV2 } from 'services/downloadsApi'
import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'

export default function ContactExportModal(props: ExportModalProps) {
  const contactColumnHelper = createExportColumnHelper({
    columns: CONTACTS_COLUMNS,
  })

  const { enableEmailIntegration } = useFeatureFlagService()
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const { startDownload } = useDownload()

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Contact Information',
        elements: [
          contactColumnHelper.retrieveColumn('full_name'),
          contactColumnHelper.retrieveColumn('first_name'),
          contactColumnHelper.retrieveColumn('last_name'),
          contactColumnHelper.retrieveColumn('domain'),
          contactColumnHelper.retrieveColumn('email_preferred'),
          contactColumnHelper.retrieveColumn('email_work'),
          contactColumnHelper.retrieveColumn('email_personal'),
          contactColumnHelper.retrieveColumn('email_generic'),
          contactColumnHelper.retrieveColumn('phone_number'),
          contactColumnHelper.retrieveColumn('url'),
          contactColumnHelper.retrieveColumn('company_chain_instagram_handle'),
          contactColumnHelper.retrieveColumn('linkedin_url'),
          contactColumnHelper.retrieveColumn('contact_company'),
          contactColumnHelper.retrieveColumn('title'),
          contactColumnHelper.retrieveColumn('seniority'),
          contactColumnHelper.retrieveColumn('department'),
          contactColumnHelper.retrieveColumn('contact_labels'),
        ],
      },
      {
        title: 'Activity',
        elements: [
          contactColumnHelper.retrieveColumn('created'),
          contactColumnHelper.retrieveColumn('modified'),
          contactColumnHelper.retrieveColumn('date_last_sent'),
          contactColumnHelper.retrieveColumn('date_last_received'),
          contactColumnHelper.retrieveColumn('origin_campaign'),
        ],
      },
      {
        title: 'Account Details',
        elements: [
          contactColumnHelper.retrieveColumn('sales_stages'),
          contactColumnHelper.retrieveColumn('account_owners'),
          contactColumnHelper.retrieveColumn('company_tags'),
          contactColumnHelper.retrieveColumn('deal_count'),
        ],
      },
    ]
    if (!enableEmailIntegration) {
      cols[1].elements = cols[1].elements.filter(
        (col) => col[0] !== 'days_since_sent' && col[0] !== 'days_since_reply'
      )
    }
    return cols
  }, [contactColumnHelper, enableEmailIntegration])

  const onSubmit = async (
    selectedCols: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await startDownload({
      items: [{ id: 0, name: 'Exporting Contacts...' }],
      initiate: async () => {
        const { sort, ...filterWithoutSort } = (props.filters ?? {}) as Record<
          string,
          unknown
        >
        const response = await startContactsExportV2({
          selected_columns: selectedCols,
          export_type: exportType,
          filter_params: {
            ...filterWithoutSort,
            include_ids: props.selectedIds ?? [],
            exclude_ids: props.excludeIds ?? [],
          },
          sort: sort as string | undefined,
        })
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getDownloadStatus,
      label: 'contacts',
    })
  }

  return (
    <BaseExportModal
      title="Select Columns for Contacts Download"
      subTitle="Download up to 10,000 contacts at a time, with any of the following columns. Any filters already applied will remain active."
      submitText="Download Contacts"
      columns={columns}
      selectedColumns={selectedColumns}
      onSubmit={onSubmit}
      show={props.show}
      handleClose={props._handleClose}
    />
  )
}
